import {
  type AnnulOperation,
  type BranchChangeset,
  type ConfigurationValidationError,
  isEnumLiteralCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { LayerKey, OperationLayerMap } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import {
  findActiveAnnulments,
  indexAnnulmentOperations,
  indexChangesetOperations,
} from '../annulOperations/helpers'
import type { ValidationErrorWithLocation } from '../errors/types'
import { userActionsForOperation } from '../helpers'
import { isOperationValidationError, type WorkspaceOperationOf } from '../types'
import type { ConfiguredEnumLiteral } from './types'

export const useConfiguredEnumLiteralsFromChangeset = ({
  configurationChangesets,
  errors,
  draftAnnulOperations,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[]
  draftAnnulOperations: WorkspaceOperationOf<AnnulOperation>[]
}): Map<string, OperationLayerMap<ConfiguredEnumLiteral[]>> => {
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name

  return useMemo(() => {
    const indexedLiteralsMap = new Map<
      string,
      OperationLayerMap<ConfiguredEnumLiteral[]>
    >()

    const allEnumOperationsWithLayerAndHistory = (
      configurationChangesets ?? []
    ).map(
      ({ changeset, changesetHistory, block }) =>
        [
          (changeset?.operations ?? [])
            .filter(notEmpty)
            .filter(isEnumLiteralCreateOperation),
          new LayerKey({
            tenantId: changeset?.tenantId,
            layerId: changeset?.layer,
          }),
          block ?? rootBlock,
          changesetHistory,
        ] as const,
    )

    const indexedOperations = indexChangesetOperations(
      configurationChangesets ?? [],
    )
    const indexedAnnulments = indexAnnulmentOperations({
      changesets: configurationChangesets ?? [],
      baseBlock: rootBlock,
    })

    allEnumOperationsWithLayerAndHistory.forEach(
      ([operations, layer, block, history]) => {
        operations.forEach((operation) => {
          const literalBlock = block
          const actions = userActionsForOperation(operation, history)
          const literalWithoutAnnulments = {
            internalOperation: operation,
            changesetHistory: history,
            layer,
            block: literalBlock,
            isDraft: false,
            type: 'configured' as const,
            errors: (errors ?? []).filter(
              ({ error }) =>
                isOperationValidationError(error) &&
                error.operationKey === operation.operationKey,
            ),
            actions,
          }

          const annulOperations = findActiveAnnulments({
            changesetAnnulments: indexedAnnulments,
            operation: literalWithoutAnnulments,
            operationIndexes: indexedOperations,
            workspaceAnnulments: draftAnnulOperations,
          })

          const configuredLiteral = {
            ...literalWithoutAnnulments,
            annulOperations,
          } satisfies ConfiguredEnumLiteral

          const blockLayers =
            indexedLiteralsMap.get(literalBlock) ??
            new OperationLayerMap<ConfiguredEnumLiteral[]>()

          const layerLiterals = blockLayers.get(layer) ?? []

          layerLiterals.push(configuredLiteral)
          blockLayers.set(layer, layerLiterals)
          indexedLiteralsMap.set(literalBlock, blockLayers)
        })
      },
    )

    return indexedLiteralsMap
  }, [draftAnnulOperations, configurationChangesets, errors, rootBlock])
}
